import awsConfig from '../config/aws-config';

// Cache for CloudFront URLs to avoid regenerating them
const urlCache = new Map();

/**
 * Normalizes storage keys the same way as CloudFront image requests (public/ prefix, etc.).
 */
export function normalizePublicImageKey(imageKey) {
  if (!imageKey?.trim?.()) {
    return '';
  }
  let cleanKey = imageKey;
  if (cleanKey.includes('/public/public/')) {
    cleanKey = cleanKey.replace('/public/public/', '/public/');
  }
  if (cleanKey.includes('profile-pictures/')) {
    if (!cleanKey.startsWith('public/')) {
      cleanKey = `public/${cleanKey}`;
    }
  } else if (cleanKey.includes('/public/profile-pictures/')) {
    cleanKey = cleanKey;
  } else {
    if (!cleanKey.startsWith('public/')) {
      cleanKey = `public/${cleanKey}`;
    }
  }
  return cleanKey;
}

/**
 * Direct S3 object URL (no Lambda resize). Matches Amplify getUrl target; use when CloudFront transform fails.
 * Note: only works for visitors if the object/bucket policy allows anonymous GET (same as unauthenticated studio views).
 */
export function getDirectS3StudioImageUrl(imageKey) {
  const cleanKey = normalizePublicImageKey(imageKey);
  if (!cleanKey) return '';
  return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
}

/**
 * If `urlString` is an HTTP(S) URL pointing at this app's configured S3 bucket,
 * returns the object key (path inside the bucket). Otherwise returns null.
 * Full S3 URLs must go through here; otherwise getCloudFrontImageUrl would bypass
 * WebP/resize and return the raw JPEG/PNG URL unchanged.
 */
export function extractStorageKeyFromAppS3Url(urlString) {
  if (!urlString || typeof urlString !== 'string') {
    return null;
  }
  const trimmed = urlString.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    const bucket = awsConfig.Storage.S3.bucket;
    const region = awsConfig.Storage.S3.region;
    const virtualHostedHosts = new Set([
      `${bucket}.s3.${region}.amazonaws.com`,
      `${bucket}.s3.dualstack.${region}.amazonaws.com`,
    ]);

    let objectPath = '';
    if (virtualHostedHosts.has(url.hostname)) {
      objectPath = url.pathname.replace(/^\/+/, '');
    } else if (url.hostname === `s3.${region}.amazonaws.com`) {
      const segments = url.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      if (segments[0] === bucket && segments.length > 1) {
        objectPath = segments.slice(1).join('/');
      }
    }

    if (!objectPath) {
      return null;
    }

    try {
      return decodeURIComponent(objectPath);
    } catch {
      return objectPath;
    }
  } catch {
    return null;
  }
}

function jsonRequestToCloudFrontToken(imageRequest) {
  const jsonStr = JSON.stringify(imageRequest);
  try {
    return btoa(unescape(encodeURIComponent(jsonStr)));
  } catch {
    return btoa(jsonStr);
  }
}

const CLOUD_FRONT_MEDIA_HOST = 'd23shqpev1klq3.cloudfront.net';

/** Gallery grid thumbnails: full dimensions (no resize), lower WebP quality only. */
export const STUDIO_GALLERY_GRID_IMAGE_OPTIONS = {
  quality: 80,
  format: 'webp',
};

/** Fullscreen carousel. Width-only (no fit crop); loaded when carousel opens / light prefetch. */
export const STUDIO_GALLERY_CAROUSEL_IMAGE_OPTIONS = {
  width: 1200,
  quality: 85,
  format: 'webp',
};

export function getStudioGalleryGridImageUrl(imageKey) {
  return getCloudFrontImageUrl(imageKey, STUDIO_GALLERY_GRID_IMAGE_OPTIONS);
}

export function getStudioGalleryCarouselImageUrl(imageKey) {
  return getCloudFrontImageUrl(imageKey, STUDIO_GALLERY_CAROUSEL_IMAGE_OPTIONS);
}

/**
 * Generates an optimized CloudFront URL for images.
 *
 * @param {string} imageKey - S3 key or app bucket HTTPS URL
 * @param {object} [options]
 * @param {number} [options.width] - Max width in px (with height omitted, aspect ratio is kept)
 * @param {number} [options.height] - Max height in px
 * @param {'cover'|'contain'|'inside'|'outside'|'fill'} [options.fit='cover'] - Used when width and height are both set
 * @param {number} [options.quality=80] - 1–100 for webp/jpeg
 * @param {'webp'|'jpeg'|'png'} [options.format='webp']
 */
export const getCloudFrontImageUrl = (imageKey, options = {}) => {
  if (!imageKey && imageKey !== 0) return '';
  const cloudFrontUrl = 'https://d23shqpev1klq3.cloudfront.net/';
  const outputFormat = options.format || 'webp';
  const quality = Math.max(1, Math.min(100, Number(options.quality ?? 80)));
  const hasResize =
    Number.isFinite(Number(options.width)) ||
    Number.isFinite(Number(options.height));
  const width = Number.isFinite(Number(options.width)) ? Number(options.width) : null;
  const height = Number.isFinite(Number(options.height)) ? Number(options.height) : null;
  const hasBothDimensions = Boolean(width && height);
  const fit = options.fit || (hasBothDimensions ? 'cover' : 'inside');
  const fitCacheKey = hasBothDimensions || options.fit ? fit : 'no-fit';

  let resolvedKey = typeof imageKey === 'string' ? imageKey.trim() : String(imageKey);
  if (!resolvedKey) {
    return '';
  }

  if (resolvedKey.startsWith('data:')) {
    return resolvedKey;
  }

  /** Output of this CDN is already transformed (possibly WebP); do not double-encode */
  if (resolvedKey.includes(CLOUD_FRONT_MEDIA_HOST)) {
    const cacheKey = `${resolvedKey}-${hasResize ? width ?? 'auto' : 'orig'}-${hasResize ? height ?? 'auto' : 'orig'}-${hasResize ? fitCacheKey : 'no-resize'}-${outputFormat}`;
    if (urlCache.has(cacheKey)) {
      return urlCache.get(cacheKey);
    }
    urlCache.set(cacheKey, resolvedKey);
    return resolvedKey;
  }

  if (/^https?:\/\//i.test(resolvedKey)) {
    const extracted = extractStorageKeyFromAppS3Url(resolvedKey);
    if (extracted) {
      resolvedKey = extracted;
    } else {
      return resolvedKey;
    }
  }

  const cacheKey = `${resolvedKey}-${hasResize ? width ?? 'auto' : 'orig'}-${hasResize ? height ?? 'auto' : 'orig'}-${hasResize ? fitCacheKey : 'no-resize'}-q${quality}-${outputFormat}`;

  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  try {
    if (!resolvedKey?.trim?.()) {
      return '';
    }

    // Some records already contain the CloudFront-encoded request token (starts with eyJ...)
    if (resolvedKey.startsWith('eyJ')) {
      const finalUrl = `${cloudFrontUrl}${resolvedKey}`;
      urlCache.set(cacheKey, finalUrl);
      return finalUrl;
    }

    const cleanKey = normalizePublicImageKey(resolvedKey);
    if (!cleanKey) {
      return '';
    }

    const formatEdits =
      outputFormat === 'jpeg'
        ? { jpeg: { quality } }
        : outputFormat === 'png'
          ? {}
          : { webp: { quality } };

    const edits = {
      toFormat: outputFormat,
      ...formatEdits,
      // Temporarily disabled because the current CloudFront image handler fails
      // for some images when EXIF auto-rotation is requested.
      // rotate: null,
    };

    if (hasResize) {
      edits.resize = {
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      };
      // Only send fit when both dimensions are set; width/height-only keeps aspect ratio.
      if (hasBothDimensions || options.fit) {
        edits.resize.fit = fit;
      }
    }

    const imageRequest = {
      bucket: awsConfig.Storage.S3.bucket,
      key: cleanKey,
      edits
    };

    const base64Request = jsonRequestToCloudFrontToken(imageRequest);
    const finalUrl = `${cloudFrontUrl}${base64Request}`;

    urlCache.set(cacheKey, finalUrl);

    return finalUrl;
  } catch (error) {
    try {
      const directUrl = getDirectS3StudioImageUrl(resolvedKey);
      urlCache.set(cacheKey, directUrl);
      return directUrl;
    } catch (fallbackError) {
      urlCache.set(cacheKey, '');
      return '';
    }
  }
};

/**
 * CloudFront / image handler URL that returns the original object (no resize).
 * Use when resize requests fail (e.g. Sharp/Lambda 500) but the source file is valid.
 */
export function getCloudFrontOriginalImageUrl(imageKey) {
  if (!imageKey?.trim?.()) {
    return '';
  }

  let resolved = imageKey.trim();
  if (/^https?:\/\//i.test(resolved)) {
    const extracted = extractStorageKeyFromAppS3Url(resolved);
    if (extracted) {
      resolved = extracted;
    } else {
      return resolved;
    }
  }

  const cleanKey = normalizePublicImageKey(resolved);
  if (!cleanKey) {
    return '';
  }
  const cloudFrontUrl = 'https://d23shqpev1klq3.cloudfront.net/';
  const imageRequest = {
    bucket: awsConfig.Storage.S3.bucket,
    key: cleanKey,
  };
  const base64Request = jsonRequestToCloudFrontToken(imageRequest);
  return `${cloudFrontUrl}${base64Request}`;
}

// Add a utility function to clear the cache when needed
export const clearImageUrlCache = () => {
  urlCache.clear();
};

// Add a utility function to check if an image key is valid
export const isValidImageKey = (imageKey) => {
  return Boolean(imageKey && typeof imageKey === 'string' && imageKey.trim() !== '');
};
